// Legal — záznam o činnostech zpracování a šablony smluv (jen owner), 19.10.2026.
// Varianta A schválená Láďou: jedna položka v menu, uvnitř záložky.
// Backend: /api/legal. Každá úprava ukládá novou verzi, historie zůstává.

import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Scale, History, Pencil, Plus, FileText, Upload, CalendarClock } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import Card from "../../components/Card.jsx";
import { Stranka, Zalozky, Modal, Field, Stitek, ErrorNote, SkeletonRows, EmptyState } from "../../components/ui.jsx";
import { apiFetch } from "../../lib/api.js";
import { toast } from "../../components/Toast.jsx";

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(String(iso).length === 10 ? `${iso}T12:00:00` : iso);
  return isNaN(d) ? "" : `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
};

// Tabulky v markdownu se na telefonu posouvají do strany uvnitř vlastního obalu.
const MD = {
  table: ({ children }) => (
    <div className="not-prose my-4 overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-left sm:min-w-[560px] text-[13px]">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border-b border-slate-200 bg-slate-50 px-3 py-2 font-semibold text-slate-700">{children}</th>,
  td: ({ children }) => <td className="border-b border-slate-100 px-3 py-2 align-top text-slate-700">{children}</td>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noreferrer" className="text-brand-deep underline underline-offset-2">{children}</a>,
};

function Text({ md }) {
  return (
    <article className="prose prose-slate max-w-none text-[15px] prose-headings:font-bold prose-h2:mt-6 prose-h2:text-lg prose-h3:text-base prose-p:leading-relaxed prose-li:leading-relaxed">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{md || ""}</ReactMarkdown>
    </article>
  );
}

async function api(body) {
  const r = await apiFetch("/api/legal", { method: "POST", body: JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || "Uložení se nepodařilo.");
  return j;
}

// Načte text z nahraného souboru (.md nebo .txt) v prohlížeči.
function nactiSoubor(file, onText) {
  if (!file) return;
  if (!/\.(md|txt|markdown)$/i.test(file.name)) { toast("Nahrajte prosím soubor .md nebo .txt.", "error"); return; }
  const r = new FileReader();
  r.onload = () => onText(String(r.result || ""));
  r.readAsText(file, "utf-8");
}

/* Okno: nová verze dokumentu (úprava textu nebo nahrání souboru). */
function OknoVerze({ dokument, puvodni, onClose, onUlozeno }) {
  const [text, setText] = useState(puvodni || "");
  const [platneOd, setPlatneOd] = useState("");
  const [poznamka, setPoznamka] = useState("");
  const [uklada, setUklada] = useState(false);
  const ulozit = async () => {
    setUklada(true);
    try {
      const j = await api({ akce: "ulozit", id: dokument.id, obsah_md: text, platne_od: platneOd || null, poznamka });
      toast(`Uloženo jako verze ${j.verze}.`, "success");
      onUlozeno();
    } catch (e) { toast(e.message, "error"); }
    finally { setUklada(false); }
  };
  return (
    <Modal open onClose={onClose} size="lg" title={`Nová verze: ${dokument.nazev}`}
      footer={
        <div className="flex w-full flex-wrap justify-end gap-2">
          <button type="button" onClick={onClose} className="btn btn-secondary">Zrušit</button>
          <button type="button" onClick={ulozit} disabled={uklada || !text.trim()} className="btn btn-primary">{uklada ? "Ukládám…" : "Uložit novou verzi"}</button>
        </div>
      }>
      <div className="space-y-3">
        <label className="flex min-h-[44px] cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-brand/30 bg-brand-50/40 px-3 text-sm font-semibold text-brand-deep">
          <Upload className="h-4 w-4" /> Nahrát soubor .md nebo .txt
          <input type="file" accept=".md,.txt,.markdown,text/markdown,text/plain" className="hidden" onChange={(e) => nactiSoubor(e.target.files?.[0], setText)} />
        </label>
        <Field label="Text (markdown)">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={16}
            className="w-full rounded-xl border border-slate-300 p-3 font-mono text-[13px] leading-relaxed" />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Platí od (nepovinné)">
            <input type="date" value={platneOd} onChange={(e) => setPlatneOd(e.target.value)} className="min-h-[44px] w-full rounded-xl border border-slate-300 px-3" />
          </Field>
          <Field label="Co se změnilo">
            <input type="text" value={poznamka} onChange={(e) => setPoznamka(e.target.value)} placeholder="např. doplněn bod 8.2" className="min-h-[44px] w-full rounded-xl border border-slate-300 px-3" />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

/* Okno: historie verzí, klik na verzi ukáže její text. */
function OknoHistorie({ dokument, onClose }) {
  const [data, setData] = useState(null);
  const [vybrana, setVybrana] = useState(null);
  useEffect(() => {
    let zivy = true;
    const q = vybrana ? `&verze=${vybrana}` : "";
    apiFetch(`/api/legal?id=${dokument.id}${q}`).then((r) => r.json()).then((j) => { if (zivy) setData(j); }).catch(() => {});
    return () => { zivy = false; };
  }, [dokument.id, vybrana]);
  return (
    <Modal open onClose={onClose} size="lg" title={`Historie verzí: ${dokument.nazev}`}>
      {!data ? <SkeletonRows rows={4} /> : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {(data.verze || []).map((v) => (
              <button key={v.verze} type="button" onClick={() => setVybrana(v.verze)}
                aria-pressed={data.aktualni?.verze === v.verze}
                className={`min-h-[44px] rounded-xl border px-3 text-left text-sm sm:min-h-0 sm:py-1.5 ${data.aktualni?.verze === v.verze ? "border-brand bg-brand text-white" : "border-slate-300 bg-white text-slate-700"}`}>
                <b>Verze {v.verze}</b> <span className="opacity-80">{fmt(v.vytvoreno)}</span>
              </button>
            ))}
          </div>
          {data.aktualni && (
            <>
              <div className="flex flex-wrap gap-2">
                {data.aktualni.platne_od && <Stitek ton="ok">Platí od {fmt(data.aktualni.platne_od)}</Stitek>}
                {data.aktualni.poznamka && <Stitek>{data.aktualni.poznamka}</Stitek>}
                {data.aktualni.autor && <Stitek>{data.aktualni.autor}</Stitek>}
              </div>
              <Text md={data.aktualni.obsah_md} />
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

/* Okno: nová smlouva. */
function OknoNovaSmlouva({ onClose, onUlozeno }) {
  const [nazev, setNazev] = useState("");
  const [popis, setPopis] = useState("");
  const [text, setText] = useState("");
  const [platneOd, setPlatneOd] = useState("");
  const [uklada, setUklada] = useState(false);
  const ulozit = async () => {
    setUklada(true);
    try {
      await api({ akce: "nova-smlouva", nazev, popis, obsah_md: text, platne_od: platneOd || null });
      toast("Smlouva přidána.", "success");
      onUlozeno();
    } catch (e) { toast(e.message, "error"); }
    finally { setUklada(false); }
  };
  return (
    <Modal open onClose={onClose} size="lg" title="Přidat smlouvu"
      footer={
        <div className="flex w-full flex-wrap justify-end gap-2">
          <button type="button" onClick={onClose} className="btn btn-secondary">Zrušit</button>
          <button type="button" onClick={ulozit} disabled={uklada || !nazev.trim() || !text.trim()} className="btn btn-primary">{uklada ? "Ukládám…" : "Přidat smlouvu"}</button>
        </div>
      }>
      <div className="space-y-3">
        <Field label="Název"><input type="text" value={nazev} onChange={(e) => setNazev(e.target.value)} placeholder="např. Klub FitWay40" className="min-h-[44px] w-full rounded-xl border border-slate-300 px-3" /></Field>
        <Field label="Popis (nepovinné)"><input type="text" value={popis} onChange={(e) => setPopis(e.target.value)} className="min-h-[44px] w-full rounded-xl border border-slate-300 px-3" /></Field>
        <label className="flex min-h-[44px] cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-brand/30 bg-brand-50/40 px-3 text-sm font-semibold text-brand-deep">
          <Upload className="h-4 w-4" /> Nahrát soubor .md nebo .txt
          <input type="file" accept=".md,.txt,.markdown,text/markdown,text/plain" className="hidden" onChange={(e) => nactiSoubor(e.target.files?.[0], setText)} />
        </label>
        <Field label="Text smlouvy (markdown)">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={12} className="w-full rounded-xl border border-slate-300 p-3 font-mono text-[13px] leading-relaxed" />
        </Field>
        <Field label="Platí od (nepovinné)"><input type="date" value={platneOd} onChange={(e) => setPlatneOd(e.target.value)} className="min-h-[44px] w-full rounded-xl border border-slate-300 px-3 sm:w-auto" /></Field>
      </div>
    </Modal>
  );
}

/* Okno: přečíst smlouvu (aktuální verze) s akcemi. */
function OknoSmlouva({ smlouva, onClose, onHistorie, onUpravit }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let zivy = true;
    apiFetch(`/api/legal?id=${smlouva.id}`).then((r) => r.json()).then((j) => { if (zivy) setData(j); }).catch(() => {});
    return () => { zivy = false; };
  }, [smlouva.id]);
  return (
    <Modal open onClose={onClose} size="lg" title={smlouva.nazev}
      footer={
        <div className="flex w-full flex-wrap justify-end gap-2">
          <button type="button" onClick={onHistorie} className="btn btn-secondary"><History className="h-4 w-4" /> Historie verzí</button>
          <button type="button" onClick={() => onUpravit(data?.aktualni?.obsah_md || "")} disabled={!data} className="btn btn-primary"><Upload className="h-4 w-4" /> Nahrát novou verzi</button>
        </div>
      }>
      {!data ? <SkeletonRows rows={6} /> : <Text md={data.aktualni?.obsah_md} />}
    </Modal>
  );
}

export default function Legal() {
  const [zalozka, setZalozka] = useState("zaznam");
  const [data, setData] = useState(null);
  const [chyba, setChyba] = useState("");
  const [okno, setOkno] = useState(null); // { typ, dokument, puvodni }

  const nacti = () => {
    setChyba("");
    apiFetch("/api/legal").then(async (r) => {
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Načtení se nepodařilo.");
      setData(j);
    }).catch((e) => setChyba(e.message));
  };
  useEffect(nacti, []);

  const z = data?.zaznam;
  const smlouvy = data?.smlouvy || [];
  const poUlozeni = () => { setOkno(null); nacti(); };

  return (
    <Stranka>
      <GradientHeader icon={Scale} title="Legal"
        napoveda="Záznam o činnostech zpracování podle GDPR a šablony smluv s klientkami. Každá úprava se uloží jako nová verze, starší verze zůstávají v historii." />

      <Zalozky aria-label="Část sekce Legal" value={zalozka} onChange={setZalozka}
        items={[{ key: "zaznam", label: "Záznam o činnostech" }, { key: "smlouvy", label: "Smlouvy" }]} />

      {chyba && <ErrorNote onRetry={nacti}>{chyba}</ErrorNote>}
      {!data && !chyba && <SkeletonRows rows={6} />}

      {data && zalozka === "zaznam" && (
        z ? (
          <Card lista ikona={FileText} title={z.nazev}>
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Stitek ton="ok">Verze {z.verze}<span aria-hidden className="h-3 w-px bg-current opacity-40" />{fmt(z.upraveno)}</Stitek>
                {z.revize_do && <Stitek ton={z.revize_do < new Date().toISOString().slice(0, 10) ? "problem" : "neutralni"} ikona={CalendarClock}>Příští revize<span aria-hidden className="h-3 w-px bg-current opacity-40" />{fmt(z.revize_do)}</Stitek>}
                <div className="ml-auto flex flex-wrap gap-2">
                  <button type="button" onClick={() => setOkno({ typ: "historie", dokument: z })} className="btn btn-secondary"><History className="h-4 w-4" /> Historie verzí</button>
                  <button type="button" onClick={() => setOkno({ typ: "verze", dokument: z, puvodni: z.obsah_md })} className="btn btn-primary"><Pencil className="h-4 w-4" /> Upravit</button>
                </div>
              </div>
              <Text md={z.obsah_md} />
            </div>
          </Card>
        ) : <EmptyState icon={FileText} title="Záznam zatím neexistuje" />
      )}

      {data && zalozka === "smlouvy" && (
        <Card lista ikona={FileText} title="Šablony smluv" odznak={smlouvy.length}>
          <div className="divide-y divide-slate-100">
            {smlouvy.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="text-base font-bold text-slate-900">{s.nazev}</div>
                  <div className="mt-0.5 text-[13px] text-slate-500">
                    Verze {s.verze}, {s.platne_od ? `platí od ${fmt(s.platne_od)}` : "datum platnosti nevyplněné"}
                  </div>
                  {s.poznamka && <div className="mt-0.5 text-[13px] text-slate-500">{s.poznamka}</div>}
                </div>
                <button type="button" onClick={() => setOkno({ typ: "smlouva", dokument: s })} className="btn btn-secondary">Otevřít</button>
              </div>
            ))}
            {smlouvy.length === 0 && <div className="py-4 text-sm text-slate-500">Zatím žádná smlouva.</div>}
          </div>
          <div className="pt-3">
            <button type="button" onClick={() => setOkno({ typ: "nova" })} className="btn btn-primary w-full sm:w-auto"><Plus className="h-4 w-4" /> Přidat smlouvu</button>
          </div>
        </Card>
      )}

      {okno?.typ === "verze" && <OknoVerze dokument={okno.dokument} puvodni={okno.puvodni} onClose={() => setOkno(null)} onUlozeno={poUlozeni} />}
      {okno?.typ === "historie" && <OknoHistorie dokument={okno.dokument} onClose={() => setOkno(null)} />}
      {okno?.typ === "nova" && <OknoNovaSmlouva onClose={() => setOkno(null)} onUlozeno={poUlozeni} />}
      {okno?.typ === "smlouva" && (
        <OknoSmlouva smlouva={okno.dokument} onClose={() => setOkno(null)}
          onHistorie={() => setOkno({ typ: "historie", dokument: okno.dokument })}
          onUpravit={(puvodni) => setOkno({ typ: "verze", dokument: okno.dokument, puvodni })} />
      )}
    </Stranka>
  );
}
